import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { adminApi, formatError, rules } from "@/lib/adminApi";
import { noticeStatus, useSite } from "@/lib/content";
import { Checkbox, SaveButton, SectionHeader } from "@/pages/admin/ui";

const EMPTY = { title: { tr: "", en: "" }, text: { tr: "", en: "" }, link_url: "", link_label: { tr: "", en: "" }, start_date: "", end_date: "", active: false, verified_tr: false, verified_en: false };
const STATUS = { live: "Yayında", inactive: "Pasif", unverified: "Doğrulanmadı", outOfRange: "Tarih dışı" };
const inp = "w-full rounded-xl border border-line px-4 py-2.5 text-base bg-white";

function Bi({ label, value, onChange, max, multiline, id }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {["tr", "en"].map((l) => (
        <label key={l} className="text-sm font-semibold text-wood space-y-1">
          <span>{label} · {l.toUpperCase()}</span>
          <Tag rows={multiline ? 3 : undefined} maxLength={max} value={value[l] || ""} onChange={(e) => onChange({ ...value, [l]: e.target.value })} className={`${inp} font-normal`} data-testid={`notice-form-${id}-${l}`} />
          {max && <span className="block text-xs font-normal text-ink-muted">{(value[l] || "").length}/{max}</span>}
        </label>
      ))}
    </div>
  );
}

function NoticeForm({ initial, onDone, onCancel }) {
  const [n, setN] = useState(() => JSON.parse(JSON.stringify({ ...EMPTY, ...initial, link_url: initial.link_url || "" })));
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setN({ ...n, [k]: v });

  const submit = async () => {
    if (!n.title.tr.trim()) return toast.error("Türkçe başlık zorunludur.");
    if (!n.start_date || !n.end_date) return toast.error("Başlangıç ve bitiş tarihi zorunludur.");
    if (n.end_date < n.start_date) return toast.error("Bitiş tarihi başlangıç tarihinden önce olamaz.");
    if (n.link_url.trim() && rules.url(n.link_url.trim()) !== true) return toast.error("Bağlantı https:// ile başlamalıdır.");
    setSaving(true);
    try {
      const body = { ...n, link_url: n.link_url.trim() || null };
      if (initial.notice_id) await adminApi.put(`/admin/notices/${initial.notice_id}`, body);
      else await adminApi.post("/admin/notices", body);
      toast.success("Duyuru kaydedildi");
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-line bg-white p-5 sm:p-6 space-y-5" data-testid="notice-form">
      <Bi id="title" label="Başlık" value={n.title} onChange={set("title")} max={120} />
      <Bi id="text" label="Kısa metin" value={n.text} onChange={set("text")} max={200} multiline />
      <label className="block text-sm font-semibold text-wood space-y-1">
        <span>Bağlantı adresi (isteğe bağlı)</span>
        <input value={n.link_url} onChange={(e) => set("link_url")(e.target.value)} placeholder="https://" className={`${inp} font-normal`} data-testid="notice-form-link-url" />
      </label>
      <Bi id="link-label" label="Bağlantı metni" value={n.link_label} onChange={set("link_label")} max={120} />
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm font-semibold text-wood space-y-1"><span>Başlangıç tarihi</span>
          <input type="date" value={n.start_date} onChange={(e) => set("start_date")(e.target.value)} className={`${inp} font-normal`} data-testid="notice-form-start" />
        </label>
        <label className="text-sm font-semibold text-wood space-y-1"><span>Bitiş tarihi</span>
          <input type="date" value={n.end_date} min={n.start_date || undefined} onChange={(e) => set("end_date")(e.target.value)} className={`${inp} font-normal`} data-testid="notice-form-end" />
        </label>
      </div>
      <div className="flex flex-wrap gap-x-6">
        <Checkbox id="n-active" label="Aktif" checked={n.active} onChange={set("active")} testId="notice-form-active" />
        <Checkbox id="n-vtr" label="TR Doğrulandı" checked={n.verified_tr} onChange={set("verified_tr")} testId="notice-form-verified-tr" />
        <Checkbox id="n-ven" label="EN Doğrulandı" checked={n.verified_en} onChange={set("verified_en")} testId="notice-form-verified-en" />
      </div>
      <p className="text-xs text-ink-muted">Duyuru, aktif + doğrulanmış ve bugün (İstanbul saati) tarih aralığındaysa ana sayfada görünür. Birden fazla uygunsa en yenisi gösterilir.</p>
      <div className="flex flex-wrap gap-3">
        <SaveButton onClick={submit} saving={saving} testId="notice-form-save" />
        <button type="button" onClick={onCancel} className="min-h-[48px] px-6 rounded-full border border-line font-semibold" data-testid="notice-form-cancel">Vazgeç</button>
      </div>
    </div>
  );
}

export default function NoticesSection() {
  const { notices, reload } = useSite();
  const [editing, setEditing] = useState(null);
  const done = async () => {
    setEditing(null);
    await reload();
  };
  const remove = async (n) => {
    if (!window.confirm("Bu duyuru silinsin mi?")) return;
    try {
      await adminApi.delete(`/admin/notices/${n.notice_id}`);
      toast.success("Duyuru silindi");
      await reload();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <section data-testid="admin-section-duyurular">
      <SectionHeader title="Duyurular" text="Mevsimsel duyurular ana sayfada giriş görselinin altında sade bir kart olarak gösterilir. Ziyaretçi kartı kapatabilir." />
      {editing ? (
        <NoticeForm initial={editing} onDone={done} onCancel={() => setEditing(null)} />
      ) : (
        <button type="button" onClick={() => setEditing({})} className="inline-flex items-center gap-2 min-h-[48px] px-6 rounded-full bg-forest text-cream font-semibold mb-6" data-testid="notice-new-button">
          <Plus className="h-4 w-4" aria-hidden="true" /> Yeni duyuru
        </button>
      )}
      {!editing && (
        <ul className="space-y-3 mt-2" data-testid="notice-list">
          {notices.length === 0 && <li className="rounded-2xl border border-dashed border-line bg-white p-8 text-center text-ink-muted" data-testid="notice-empty">Henüz duyuru yok.</li>}
          {notices.map((n) => (
            <li key={n.notice_id} className="rounded-2xl border border-line bg-white p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`notice-row-${n.notice_id}`}>
              <div className="min-w-0">
                <p className="font-serif text-xl font-semibold text-forest">{n.title.tr}</p>
                <p className="text-xs text-ink-muted">{n.start_date} → {n.end_date}</p>
                <div className="mt-1 flex gap-2 text-xs font-semibold">
                  <span className="rounded-full bg-stone-100 px-2 py-0.5" data-testid={`notice-row-${n.notice_id}-status-tr`}>TR: {STATUS[noticeStatus(n, "tr")]}</span>
                  <span className="rounded-full bg-stone-100 px-2 py-0.5" data-testid={`notice-row-${n.notice_id}-status-en`}>EN: {STATUS[noticeStatus(n, "en")]}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing(n)} aria-label="Düzenle" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line" data-testid={`notice-row-${n.notice_id}-edit`}><Pencil className="h-4 w-4" /></button>
                <button type="button" onClick={() => remove(n)} aria-label="Sil" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#FECDCA] text-[#B42318]" data-testid={`notice-row-${n.notice_id}-delete`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
